import React, { Suspense, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Environment, useGLTF } from '@react-three/drei';
import { SkeletonUtils } from 'three-stdlib';
import { assetUrl } from './utils/paths';
import { applyBallGoldMaterial } from './ballMaterial';

function World9Isolated() {
  const { scene } = useThree();

  useEffect(() => {
    if ('backgroundIntensity' in scene) {
      const previous = scene.backgroundIntensity ?? 1;
      scene.backgroundIntensity = 1;
      return () => {
        scene.backgroundIntensity = previous;
      };
    }

    return undefined;
  }, [scene]);

  const files = [
    '/nxp.webp',
    '/pxp.webp',
    '/pyp.webp',
    '/nyp.webp',
    '/nzp.webp',
    '/pzp.webp',
  ].map(assetUrl);

  return (
    <Environment
      files={files}
      environmentIntensity={0.7}
    />
  );
}

function BallIsolated({ onReady }) {
  const groupRef = useRef();
  const { camera } = useThree();
  const { scene: sourceScene } = useGLTF(assetUrl('/ball.glb'));
  const scene = useMemo(
    () => (sourceScene ? SkeletonUtils.clone(sourceScene) : null),
    [sourceScene]
  );

  useLayoutEffect(() => {
    if (!scene) return;
    applyBallGoldMaterial(scene);
  }, [scene]);

  useEffect(() => {
    if (typeof onReady === 'function') {
      onReady();
    }
  }, [onReady]);

  useFrame(() => {
    if (!groupRef.current) return;

    const offset = new THREE.Vector3(0, -0.46, -2);
    groupRef.current.position
      .copy(camera.position)
      .add(offset.applyQuaternion(camera.quaternion));
    groupRef.current.quaternion.copy(camera.quaternion);
  });

  if (!scene) return null;

  return (
    <group ref={groupRef} scale={1}>
      <primitive object={scene} />
    </group>
  );
}

useGLTF.preload(assetUrl('/ball.glb'));

export function BallOnlyApp({ onSceneReady }) {
  return (
    <div style={{ width: '100vw', height: '100vh', background: '#000' }}>
      <Canvas
        flat
        camera={{ fov: 75, position: [0, 0, 20] }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true }}
      >
        <color attach="background" args={['#000000']} />
        <World9Isolated />
        <ambientLight intensity={5} />
        <directionalLight intensity={5} position={[0, -4, -2]} />
        <directionalLight intensity={5} position={[0, -1, 7]} />
        <Suspense fallback={null}>
          <BallIsolated onReady={onSceneReady} />
        </Suspense>
      </Canvas>
    </div>
  );
}

export default BallOnlyApp;
